/**
 * Smart Search Component
 *
 * Natural language search interface for querying personnel records.
 * Supports voice input, query suggestions, and parsed filter display.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { nlQueryService, ParsedQuery, QueryFilter } from '../../services/ai';
import styles from './SmartSearch.module.css';

interface SmartSearchProps {
  onSearch: (query: ParsedQuery) => void;
  placeholder?: string;
  className?: string;
}

export function SmartSearch({ onSearch, placeholder, className }: SmartSearchProps) {
  const [query, setQuery] = useState('');
  const [parsedQuery, setParsedQuery] = useState<ParsedQuery | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInterface | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognitionAPI) {
      recognitionRef.current = new SpeechRecognitionAPI();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-GH';

      recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setQuery(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Update suggestions as user types
  useEffect(() => {
    if (query.length > 0) {
      const newSuggestions = nlQueryService.getSuggestions(query);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setSuggestions(nlQueryService.getSuggestions(''));
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [query]);

  // Parse query in real-time
  useEffect(() => {
    if (query.trim().length > 3) {
      const parsed = nlQueryService.parseQuery(query);
      setParsedQuery(parsed);
    } else {
      setParsedQuery(null);
    }
  }, [query]);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    if (query.trim()) {
      const parsed = nlQueryService.parseQuery(query);
      onSearch(parsed);
      setShowSuggestions(false);
    }
  }, [query, onSearch]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    const parsed = nlQueryService.parseQuery(suggestion);
    onSearch(parsed);
  }, [onSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  }, [showSuggestions, suggestions, selectedIndex, handleSuggestionClick]);

  const toggleVoice = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  const clearQuery = useCallback(() => {
    setQuery('');
    setParsedQuery(null);
    inputRef.current?.focus();
  }, []);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <form onSubmit={handleSubmit} className={styles.searchForm}>
        <div className={styles.inputWrapper}>
          <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query && setShowSuggestions(true)}
            placeholder={placeholder || 'Search records... Try "Show all PFiles checked out for more than 30 days"'}
            className={styles.input}
            autoComplete="off"
          />

          {query && (
            <button type="button" onClick={clearQuery} className={styles.clearButton} title="Clear">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}

          {recognitionRef.current && (
            <button
              type="button"
              onClick={toggleVoice}
              className={`${styles.voiceButton} ${isListening ? styles.listening : ''}`}
              title={isListening ? 'Stop listening' : 'Voice search'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
          )}

          <button type="submit" className={styles.submitButton}>
            Search
          </button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className={styles.suggestions}>
            <div className={styles.suggestionsHeader}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Suggestions
            </div>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className={`${styles.suggestionItem} ${index === selectedIndex ? styles.selected : ''}`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </form>

      {/* Parsed query display */}
      {parsedQuery && parsedQuery.filters.length > 0 && (
        <div className={styles.parsedQuery}>
          <div className={styles.parsedHeader}>
            <span className={styles.confidence} title="Query confidence">
              {parsedQuery.confidence}% match
            </span>
            <span className={styles.intentBadge}>{parsedQuery.intent}</span>
            <span className={styles.entityBadge}>{parsedQuery.entityType}</span>
          </div>
          <div className={styles.filters}>
            {parsedQuery.filters.map((filter, index) => (
              <FilterChip key={index} filter={filter} />
            ))}
            {parsedQuery.timeRange && (
              <span className={styles.filterChip}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {parsedQuery.timeRange.relative || 'Custom date range'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ filter }: { filter: QueryFilter }) {
  return (
    <span className={styles.filterChip}>
      <span className={styles.filterField}>{filter.field}:</span>
      <span className={styles.filterValue}>{filter.displayValue || String(filter.value)}</span>
    </span>
  );
}

// Type declarations for Web Speech API
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
  readonly resultIndex: number;
}

interface SpeechRecognitionInterface extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognitionInterface;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}
